import { all, call, put, select, takeEvery, takeLatest, debounce, delay } from 'redux-saga/effects';

import {
  getRemoteAssetInfo as getRemoteAssetInfoApi,
  getComponents as getComponentsApi,
  getSiteComponentTree,
  getComponentsStatus as getComponentsStatusApi,
  createComponent as createComponentApi,
  updateComponent as updateComponentApi,
  deleteComponent as deleteComponentApi,
  deleteNonSiteComponents as deleteNonSiteComponentsApi,
  downloadComponentsExcelTemplate as downloadCompsExcelApi,
  upsertComponentsInBulk as upsertComponentsInBulkApi,
  createVariables as createVariablesApi,
  updateVariable as updateVariableApi,
  deleteVariable as deleteVariableApi,
} from '../../services';
import { downloadBlob, sanitizeFileName } from '../../utils';
import { INTERNAL_DATA_SOURCE } from '../../constants';
import config from '../../config';
import {
  REFRESH_VALUES,
  setPollingActive,
  setPollingActiveDone,
  afterSiteCleared,
} from '../application';
import { getSources } from '../sources';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import {
  REQUEST_COMPONENTS,
  CREATE_COMPONENT,
  DELETE_COMPONENT,
  UPSERT_COMPONENTS,
  DELETE_NONSITE_COMPONENTS,
  UPDATE_COMPONENT,
  DOWNLOAD_COMPONENTS_TEMPLATE,
  REQUEST_REMOTE_ASSET_INFO,
  receiveComponents,
  receiveStatus,
  receiveCreatedComponent,
  setImportErrors,
  receiveRemoteAssetInfo,
} from './actions';
import { findActiveAncestorIds, findActiveDescendantIds } from './helpers';
import { getComponentsStatus } from './selectors';

export function* doGetComponentsStatus(siteId) {
  if (config.onPremise) return;
  try {
    const { values: newHealth } = yield call(getComponentsStatusApi, siteId);
    const oldHealth = yield select(getComponentsStatus);
    const changed = JSON.stringify(oldHealth) !== JSON.stringify(newHealth);
    const bothEmpty = (oldHealth?.length ?? 0) === 0 && (newHealth?.length ?? 0) === 0;

    if (changed || bothEmpty) {
      yield put(receiveStatus(newHealth));
    }
  } catch (error) {
    console.error('Unable to fetch component status:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getComponentsStatus', 'error')()));
  }
}

export function* doForceGetComponentsStatus(action) {
  yield put(setPollingActive('components'));
  yield call(doGetComponentsStatus, action.siteId);
  yield put(setPollingActiveDone('components'));
}

export function* doRequestComponents(action) {
  const { siteId, imported = false } = action;

  try {
    const { values: newComps } = yield call(getComponentsApi, siteId);
    const { data: tree } = yield call(getSiteComponentTree, siteId);

    if (newComps?.length) {
      const updatedHashmap = {};
      for (const comp of newComps) {
        updatedHashmap[comp.id] = comp;
      }
      const compsWithRelations = yield all(
        newComps.map((comp) => ({
          ...comp,
          ancestorIds: findActiveAncestorIds(comp, newComps),
          descendantIds: findActiveDescendantIds(comp, tree),
        }))
      );

      yield put(
        receiveComponents(
          compsWithRelations,
          updatedHashmap,
          tree,
          siteId,
          imported ? Date.now() : 0
        )
      );
      yield call(doGetComponentsStatus, siteId);
    } else {
      yield put(receiveComponents([], {}, {}, siteId, 0));
      yield call(doGetComponentsStatus, siteId);
    }
  } catch (error) {
    console.error('Unable to fetch components:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getComponents', 'error')()));
    yield put(receiveComponents([], {}, {}, siteId, 0));
  }
}

export function* doCrudComponentAttributes(
  { updates = [], creates = [], deletes = [] },
  component,
  action
) {
  const sources = yield select(getSources);
  const internalSource = sources.find((source) => source.type === INTERNAL_DATA_SOURCE);

  if (!internalSource) {
    yield put(displayNotification(getNotification('noInternalSource', 'error')()));
    return false;
  }

  if (!component.asset?.id) {
    yield put(displayNotification(getNotification('noComponentAsset', 'error')()));
    return false;
  }

  try {
    yield all(
      updates.map((variableData) =>
        call(updateVariableApi, internalSource.id, variableData.id, variableData)
      )
    );
  } catch (error) {
    console.error('Unable to update component attributes:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('updateComponentAttributes', 'error')(
          component.name,
          component.itemDesignation,
          action
        )
      )
    );
    return false;
  }

  try {
    const { errors } = yield call(createVariablesApi, internalSource.id, creates);
    if (errors.length > 0) {
      throw new Error(JSON.stringify(errors));
    }
  } catch (error) {
    console.error('Unable to create component attributes:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('createComponentAttributes', 'error')(
          component.name,
          component.itemDesignation,
          action
        )
      )
    );
    return false;
  }

  try {
    yield all(
      deletes.map((variableDeleteId) =>
        call(deleteVariableApi, internalSource.id, variableDeleteId)
      )
    );
  } catch (error) {
    console.error('Unable to delete component attributes:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('deleteComponentAttributes', 'error')(
          component.name,
          component.itemDesignation,
          action
        )
      )
    );
    return false;
  }
  return true;
}

export function* doCreateComponent(action) {
  const { siteId, org, data, doNotify } = action;
  const { attributes, component } = data;
  let newComponent;
  let handledAttributes;

  try {
    newComponent = yield createComponentApi({ ...component, site: siteId, org });
  } catch (error) {
    console.error('Unable to create component:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createComponent', 'error')(component.name)));
    return;
  }

  if (attributes) {
    //  Need to inject asset_id from new comp when creating.
    if (attributes?.creates) {
      attributes.creates = attributes.creates.map((item) => ({
        ...item,
        asset_id: newComponent.asset?.id,
      }));
    }

    handledAttributes = yield call(doCrudComponentAttributes, attributes, newComponent, 'create');
  } else {
    handledAttributes = true;
  }

  if (handledAttributes) {
    yield doRequestComponents({ siteId });
    yield put(receiveCreatedComponent(newComponent.id, newComponent.parent));
    if (doNotify) {
      yield put(
        displayNotification(
          getNotification('createComponent', 'success')(component.name, component.itemDesignation)
        )
      );
    }
  }
  return newComponent;
}

export function* doUpdateComponent(action) {
  const { id, siteId, data, doNotify } = action;
  const { attributes, component } = data;
  const componentObject = yield select((state) => state.components.hashmap[id]);

  let updatedComponent;
  let handledAttributes;

  try {
    updatedComponent = yield call(updateComponentApi, id, component);
  } catch (error) {
    console.error('Unable to update component:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('updateComponent', 'error')(
          componentObject.name,
          componentObject.itemDesignation,
          id
        )
      )
    );
  }

  if (updatedComponent && attributes) {
    handledAttributes = yield call(
      doCrudComponentAttributes,
      attributes,
      updatedComponent,
      'update'
    );
  } else if (updatedComponent) {
    handledAttributes = true;
  }

  if (handledAttributes) {
    yield call(doRequestComponents, { siteId });
    if (doNotify) {
      yield put(
        displayNotification(
          getNotification('updateComponent', 'success')(
            component?.name,
            component?.itemDesignation,
            id
          )
        )
      );
    }
    yield call(doForceGetComponentsStatus, { siteId });
  }
}

export function* doUpsertComponents(action) {
  const { siteId, org, components, doNotify, update } = action;

  try {
    const jsonBlob = new Blob([JSON.stringify(components)], { type: 'application/json' });
    const fd = new FormData();
    fd.append('file', jsonBlob, 'data.json');
    fd.append('site', siteId);
    fd.append('org', org);

    yield upsertComponentsInBulkApi(fd);
    yield doRequestComponents({ siteId, imported: true });
    yield call(doForceGetComponentsStatus, { siteId });
    if (doNotify && update) {
      yield put(displayNotification(getNotification('upsertComponents', 'success')(update)));
    }
    if (doNotify) {
      yield put(displayNotification(getNotification('upsertComponents', 'success')()));
    }
  } catch (error) {
    yield call(checkOnline);
    yield put(
      setImportErrors(
        error.errors?.upsertErrors || ['An error occured when updating/creating components']
      )
    );
    if (doNotify) {
      yield put(displayNotification(getNotification('upsertComponents', 'error')()));
    }
  }
}

export function* doDeleteComponent(action) {
  const { id, siteId } = action;
  const componentObject = yield select((state) => state.components.hashmap[id]);
  try {
    yield call(deleteComponentApi, id);
    yield call(doRequestComponents, { siteId });
    yield put(
      displayNotification(
        getNotification('deleteComponent', 'success')(
          componentObject.name,
          componentObject.itemDesignation,
          id
        )
      )
    );
  } catch (error) {
    console.error('Unable to delete component:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('deleteComponent', 'error')(
          componentObject.name,
          componentObject.itemDesignation,
          id
        )
      )
    );
  }
}

export function* doDeleteNonSiteComponents(action) {
  const { org, siteId } = action;
  try {
    yield call(deleteNonSiteComponentsApi, { org, site: siteId });
    yield call(doRequestComponents, { siteId });
    yield put(displayNotification(getNotification('deleteNonSiteComponents', 'success')));
  } catch (error) {
    console.error('Unable to delete nonsite components:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteNonSiteComponents', 'error')()));
  }
}

export function* doDownloadCompsTemplate(action) {
  const { site } = action;
  try {
    const file = yield call(downloadCompsExcelApi, { site: site.id, org: site.org });
    downloadBlob(
      file.data,
      `${sanitizeFileName(site.name)}-components_template.xlsx`,
      file.headers['content-type']
    );
  } catch (error) {
    console.error('Unable to download components excel:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('downloadCompExcel', 'error')()));
  }
}

export function* doRequestRemoteAssetInfo(action) {
  const { integrationIds, componentId } = action;
  try {
    const assetInfo = yield all(
      integrationIds.map((id) => call(getRemoteAssetInfoApi, id, componentId))
    );

    const mappedAssetInfo = assetInfo.reduce((acc, info) => {
      if (Object.keys(info).length > 1) {
        return { ...acc, ...info };
      }
      return acc;
    }, {});
    yield put(receiveRemoteAssetInfo(componentId, mappedAssetInfo));
  } catch (error) {
    console.error(error);
    return;
  }
}

export function* pollForSiteComponent(siteId) {
  while (true) {
    const { values: comps } = yield call(getComponentsApi, siteId);
    if (comps.some((comp) => comp.type === 'site')) {
      break;
    }
    yield delay(500);
  }
}

export const sagas = [
  takeLatest(REQUEST_COMPONENTS, afterSiteCleared(doRequestComponents)),
  takeLatest(REQUEST_REMOTE_ASSET_INFO, doRequestRemoteAssetInfo),

  takeLatest(CREATE_COMPONENT, doCreateComponent),
  takeLatest(UPDATE_COMPONENT, doUpdateComponent),
  takeLatest(UPSERT_COMPONENTS, doUpsertComponents),
  takeEvery(DELETE_COMPONENT, doDeleteComponent),
  takeEvery(DELETE_NONSITE_COMPONENTS, doDeleteNonSiteComponents),

  takeLatest(DOWNLOAD_COMPONENTS_TEMPLATE, doDownloadCompsTemplate),

  debounce(500, REFRESH_VALUES, afterSiteCleared(doForceGetComponentsStatus)),
];
